import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Phone, PhoneIncoming, CalendarDays, AlertTriangle, MessageSquare, CheckCircle2, Clock, Bot } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
interface NotificationsScreenProps {
  onBack: () => void;
}
interface Notification {
  id: string;
  type: 'call' | 'meeting' | 'urgent' | 'summary' | 'system';
  title: string;
  description: string;
  time: string;
  read: boolean;
  icon: React.ElementType;
  accent: string;
  accentBg: string;
}
const MOCK_NOTIFICATIONS: Notification[] = [{
  id: '1',
  type: 'urgent',
  title: 'Urgent Call — Sarah Connor',
  description: "Caller flagged as urgent. Said it's about the contract deadline tomorrow.",
  time: '2 min ago',
  read: false,
  icon: AlertTriangle,
  accent: 'text-red-500',
  accentBg: 'bg-red-50 dark:bg-red-500/10'
}, {
  id: '2',
  type: 'call',
  title: 'Call Handled — John Smith',
  description: 'AI took a message: "Please call back regarding the Q4 budget review."',
  time: '15 min ago',
  read: false,
  icon: PhoneIncoming,
  accent: 'text-blue-500',
  accentBg: 'bg-blue-50 dark:bg-blue-500/10'
}, {
  id: '3',
  type: 'meeting',
  title: 'Meeting Booked',
  description: 'Emma Watson scheduled a call for tomorrow at 2:00 PM.',
  time: '32 min ago',
  read: false,
  icon: CalendarDays,
  accent: 'text-purple-500',
  accentBg: 'bg-purple-50 dark:bg-purple-500/10'
}, {
  id: '4',
  type: 'summary',
  title: 'Call Summary Ready',
  description: "AI summary for Michael Chang's call is ready to review.",
  time: '1 hr ago',
  read: true,
  icon: MessageSquare,
  accent: 'text-vexu-600',
  accentBg: 'bg-vexu/10'
}, {
  id: '5',
  type: 'call',
  title: 'Call Handled — David Miller',
  description: 'Caller asked about pricing. AI provided standard info and offered callback.',
  time: '1 hr ago',
  read: true,
  icon: PhoneIncoming,
  accent: 'text-blue-500',
  accentBg: 'bg-blue-50 dark:bg-blue-500/10'
}, {
  id: '6',
  type: 'meeting',
  title: 'Meeting Booked',
  description: 'Lisa Ray booked a demo session for Thursday at 10:00 AM.',
  time: '2 hrs ago',
  read: true,
  icon: CalendarDays,
  accent: 'text-purple-500',
  accentBg: 'bg-purple-50 dark:bg-purple-500/10'
}, {
  id: '7',
  type: 'system',
  title: 'Weekly Report',
  description: 'Your AI handled 47 calls this week. Tap to view the full report.',
  time: '5 hrs ago',
  read: true,
  icon: Bot,
  accent: 'text-amber-500',
  accentBg: 'bg-amber-50 dark:bg-amber-500/10'
}, {
  id: '8',
  type: 'call',
  title: 'Call Handled — James Wilson',
  description: 'Left a voicemail. AI transcribed: "Following up on the partnership proposal."',
  time: '6 hrs ago',
  read: true,
  icon: PhoneIncoming,
  accent: 'text-blue-500',
  accentBg: 'bg-blue-50 dark:bg-blue-500/10'
}, {
  id: '9',
  type: 'urgent',
  title: 'Missed Urgent — Anna Davis',
  description: 'Tried reaching you 3 times. Marked as high priority.',
  time: 'Yesterday',
  read: true,
  icon: AlertTriangle,
  accent: 'text-red-500',
  accentBg: 'bg-red-50 dark:bg-red-500/10'
}];
export function NotificationsScreen({
  onBack
}: NotificationsScreenProps) {
  const {
    isLight
  } = useTheme();
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const unreadCount = notifications.filter(n => !n.read).length;
  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
  };
  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
  };
  return <div className="h-full flex flex-col relative" data-id="element-2627" data-mp="6xom6zuunrvi">
      {/* Header */}
      <div className={`px-6 pt-6 pb-4 flex items-center gap-3 ${isLight ? 'border-b border-slate-100' : 'border-b border-white/5'}`} data-id="element-2628" data-mp="6zrbtis3bizp">
        <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-2629" data-mp="3tchhlumw63vj">
          <ArrowLeft className="w-5 h-5" data-id="element-2630" data-mp="3fd8bxowbas5t" />
        </button>
        <div className="flex-1" data-id="element-2631" data-mp="3ffuipmbdp3mz">
          <h1 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2632" data-mp="3advi2k70wktq">
            Notifications
          </h1>
          {unreadCount > 0 && <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2633" data-mp="18nk0axzcipvr">
              {unreadCount} unread
            </p>}
        </div>
        {unreadCount > 0 && <button onClick={markAllRead} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-vexu bg-vexu/10 active:bg-vexu/20 transition-colors" data-id="element-2634" data-mp="1c3wmen7da4aj">
            <CheckCircle2 className="w-3 h-3" data-id="element-2635" data-mp="1kxt7dyospaod" />
            Mark all read
          </button>}
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto pb-24" data-id="element-2636" data-mp="t6d9v9lpa3jd">
        <div className="p-4 space-y-2" data-id="element-2637" data-mp="t7of7dusnjcl">
          {notifications.map((notification, index) => {
          const Icon = notification.icon;
          return <motion.button key={notification.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.03
          }} onClick={() => markAsRead(notification.id)} className={`w-full text-left flex items-start gap-3 p-3.5 rounded-2xl transition-colors ${!notification.read ? isLight ? 'bg-white border border-slate-200/60 shadow-sm' : 'bg-white/[0.05] border border-white/[0.08]' : isLight ? 'bg-white/40 border border-slate-100/60' : 'bg-white/[0.02] border border-white/[0.03]'}`} data-id="element-2638" data-mp="126ihnkut33y2" data-mpi={index}>
                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? notification.accentBg.split(' ')[0] : notification.accentBg.includes('dark:') ? notification.accentBg.split('dark:')[1] : notification.accentBg}`} data-id="element-2639" data-mp="1a5li62twg4ew">
                  <Icon className={`w-5 h-5 ${notification.accent}`} data-id="element-2640" data-mp="1f4y6yt4bzlk4" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0" data-id="element-2641" data-mp="relxvexcniry">
                  <div className="flex items-start justify-between gap-2" data-id="element-2642" data-mp="mgtv1pv62c8z">
                    <span className={`text-sm leading-tight ${!notification.read ? 'font-bold' : 'font-semibold'} ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2643" data-mp="mepvfk4avzei">
                      {notification.title}
                    </span>
                    {!notification.read && <span className="w-2 h-2 rounded-full bg-vexu flex-shrink-0 mt-1.5" data-id="element-2644" data-mp="2t52n3djf6e64" />}
                  </div>
                  <p className={`text-[12px] leading-relaxed mt-0.5 line-clamp-2 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2645" data-mp="3gvotl0drzqe0">
                    {notification.description}
                  </p>
                  <div className="flex items-center gap-1 mt-1.5" data-id="element-2646" data-mp="37w1skfoykmlh">
                    <Clock className={`w-3 h-3 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-2647" data-mp="3bu97r63rxfuk" />
                    <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2648" data-mp="1wh6d4rihmh3v">
                      {notification.time}
                    </span>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>
      </div>
    </div>;
}